'use client';
import React, { useState } from 'react';
import { Shield, User, Building2, ChevronRight, ChevronLeft, CheckCircle, Upload, FileText, Camera, Users, Info, Lock, Phone, Briefcase, RefreshCw, Trash2, Plus, X, ArrowRight, Clock, Flag, UserCheck, AlertTriangle, Landmark, Check, ShieldCheck, ScanLine, ShieldX } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';


// ─── Types ────────────────────────────────────────────────────────────────────
type InvestorType = 'individual' | 'institutional' | null;
type StepId = 'type' | 'profile' | 'documents' | 'liveness' | 'ubo' | 'suitability' | 'review';

interface FormField {
  label: string;
  key: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

interface UploadedFile {
  id: string;
  name: string;
  size: string;
  status: 'uploading' | 'scanning' | 'uploaded' | 'verified' | 'infected';
  scanResult?: 'clean' | 'infected' | 'error';
}

interface UBOEntry {
  id: string;
  name: string;
  nationality: string;
  dob: string;
  ownership: string;
  isPEP: boolean;
}

// ─── Step Config ──────────────────────────────────────────────────────────────
const INDIVIDUAL_STEPS: { id: StepId; label: string; icon: React.ElementType }[] = [
  { id: 'type', label: 'Investor Type', icon: User },
  { id: 'profile', label: 'Personal Info', icon: UserCheck },
  { id: 'documents', label: 'Identity Docs', icon: FileText },
  { id: 'liveness', label: 'Liveness Check', icon: Camera },
  { id: 'suitability', label: 'Suitability', icon: Briefcase },
  { id: 'review', label: 'Submit', icon: CheckCircle },
];

const INSTITUTIONAL_STEPS: { id: StepId; label: string; icon: React.ElementType }[] = [
  { id: 'type', label: 'Investor Type', icon: Building2 },
  { id: 'profile', label: 'Entity Info', icon: Building2 },
  { id: 'documents', label: 'Corporate Docs', icon: FileText },
  { id: 'ubo', label: 'UBO / Directors', icon: Users },
  { id: 'suitability', label: 'Suitability', icon: Briefcase },
  { id: 'review', label: 'Submit', icon: CheckCircle },
];

// ─── Upload Zone ──────────────────────────────────────────────────────────────
function UploadZone({ label, hint, onUpload }: { label: string; hint: string; onUpload: () => void }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); onUpload(); }}
      onClick={onUpload}
      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-200 ${
        dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-surface-3'
      }`}
    >
      <Upload size={20} className="mx-auto mb-2 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
  );
}

// ─── File Row ──────────────────────────────────────────────────────────────────
function FileRow({ file, onRemove }: { file: UploadedFile; onRemove: (id: string) => void }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-3 border border-border group">
      <div className="w-8 h-8 rounded-md bg-surface-4 flex items-center justify-center flex-shrink-0">
        <FileText size={14} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
        <p className="text-xs text-muted-foreground">{file.size}</p>
      </div>
      {file.status === 'uploading' && (
        <div className="flex items-center gap-1.5 text-warning text-xs">
          <RefreshCw size={12} className="animate-spin" />
          <span>Uploading…</span>
        </div>
      )}
      {file.status === 'scanning' && (
        <div className="flex items-center gap-1.5 text-info text-xs">
          <ScanLine size={12} className="animate-pulse" />
          <span>Scanning…</span>
        </div>
      )}
      {file.status === 'uploaded' && (
        <StatusBadge variant="pending" label="Uploaded" dot />
      )}
      {file.status === 'verified' && (
        <div className="flex items-center gap-1.5 text-positive text-xs">
          <ShieldCheck size={12} />
          <span>Clean</span>
        </div>
      )}
      {file.status === 'infected' && (
        <div className="flex items-center gap-1.5 text-negative text-xs">
          <ShieldX size={12} />
          <span>Threat Detected</span>
        </div>
      )}
      <button
        onClick={() => onRemove(file.id)}
        className="p-1.5 rounded hover:bg-negative/10 text-muted-foreground hover:text-negative transition-colors opacity-0 group-hover:opacity-100"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

// ─── Progress Stepper ─────────────────────────────────────────────────────────
function Stepper({ steps, currentStep }: { steps: { id: StepId; label: string; icon: React.ElementType }[]; currentStep: StepId }) {
  const currentIdx = steps.findIndex(s => s.id === currentStep);
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-1">
      {steps.map((step, idx) => {
        const isCompleted = idx < currentIdx;
        const isActive = idx === currentIdx;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div className={`flex flex-col items-center gap-1.5 flex-shrink-0 ${idx === 0 ? '' : ''}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                isCompleted ? 'bg-positive border-positive text-white' : isActive ?'bg-primary border-primary text-white': 'bg-surface-3 border-border text-muted-foreground'
              }`}>
                {isCompleted ? <Check size={16} /> : <Icon size={15} />}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${
                isActive ? 'text-foreground' : isCompleted ? 'text-positive' : 'text-muted-foreground'
              }`}>{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 flex-1 min-w-[20px] mx-1 mb-5 transition-all ${
                idx < currentIdx ? 'bg-positive' : 'bg-border'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KYCSubmissionPortal() {
  const supabase = createClient();
  const [investorType, setInvestorType] = useState<InvestorType>(null);
  const [currentStep, setCurrentStep] = useState<StepId>('type');
  const [submitted, setSubmitted] = useState(false);
  const [referenceId] = useState(`KYC-2026-${Math.floor(10000 + Math.random() * 90000)}`);

  // Individual profile state
  const [indProfile, setIndProfile] = useState({
    firstName: '', lastName: '', email: '', phone: '', dob: '',
    nationality: '', country: '', address: '', city: '', postalCode: '',
    taxId: '', pepStatus: 'no',
  });

  // Institutional profile state
  const [instProfile, setInstProfile] = useState({
    entityName: '', entityType: '', jurisdiction: '', incorporationNo: '',
    registeredAddress: '', email: '', phone: '', website: '',
    fatcaStatus: '', taxId: '', primaryContact: '',
  });

  // Documents state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Liveness state
  const [livenessStatus, setLivenessStatus] = useState<'idle' | 'pending' | 'passed'>('idle');

  // UBO state
  const [ubos, setUbos] = useState<UBOEntry[]>([
    { id: 'ubo-1', name: '', nationality: '', dob: '', ownership: '', isPEP: false },
  ]);

  // Suitability state
  const [suitability, setSuitability] = useState({
    investmentExperience: '', riskTolerance: '', investmentHorizon: '',
    annualIncome: '', netWorth: '', sourceOfFunds: '', investmentObjective: '',
    accreditedStatus: '',
  });

  const steps = investorType === 'institutional' ? INSTITUTIONAL_STEPS : INDIVIDUAL_STEPS;

  const handleAddFile = (docType: string) => {
    const fileId = `file-${Date.now()}`;
    const newFile: UploadedFile = {
      id: fileId,
      name: `${docType.replace(/\s+/g, '_')}_${Date.now()}.pdf`,
      size: `${(Math.random() * 3 + 0.5).toFixed(1)} MB`,
      status: 'scanning',
    };
    setUploadedFiles(prev => [...prev, newFile]);

    // Simulate virus scan (1.5s) then mark clean
    setTimeout(() => {
      setUploadedFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, status: 'verified', scanResult: 'clean' } : f
      ));
      // Log upload to vault audit trail
      supabase.from('vault_audit_trail').insert({
        doc_name: newFile.name,
        doc_ref: `KYC-UPLOAD-${fileId}`,
        action: 'upload',
        actor_name: 'Investor (KYC Submission)',
        actor_role: 'investor',
        details: { doc_type: docType, size: newFile.size, scan_result: 'clean', kyc_session: referenceId },
        outcome: 'success',
      }).then(() => {}).catch(() => {});
    }, 1500);
  };

  const handleRemoveFile = (id: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleAddUBO = () => {
    setUbos(prev => [...prev, {
      id: `ubo-${Date.now()}`,
      name: '', nationality: '', dob: '', ownership: '', isPEP: false,
    }]);
  };

  const handleRemoveUBO = (id: string) => {
    setUbos(prev => prev.filter(u => u.id !== id));
  };

  const handleNext = () => {
    const stepIdx = steps.findIndex(s => s.id === currentStep);
    if (stepIdx < steps.length - 1) {
      setCurrentStep(steps[stepIdx + 1].id);
    }
  };

  const handleBack = () => {
    const stepIdx = steps.findIndex(s => s.id === currentStep);
    if (stepIdx > 0) {
      setCurrentStep(steps[stepIdx - 1].id);
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  // ─── Submitted State ─────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-lg w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-positive/10 border-2 border-positive flex items-center justify-center mx-auto">
            <CheckCircle size={36} className="text-positive" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Application Submitted</h2>
            <p className="text-muted-foreground text-sm">
              Your {investorType === 'institutional' ? 'KYB' : 'KYC'} application has been received and is now in the compliance review queue.
            </p>
          </div>
          <div className="glass-card rounded-xl p-5 text-left space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Reference ID</span>
              <span className="font-mono-data text-sm font-semibold text-primary">{referenceId}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Application Type</span>
              <span className="text-sm font-medium text-foreground">{investorType === 'institutional' ? 'KYB — Corporate Entity' : 'KYC — Individual Investor'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Review Queue</span>
              <span className="text-sm font-medium text-foreground">Standard {investorType === 'institutional' ? 'KYB' : 'KYC'} Queue</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Expected SLA</span>
              <span className="text-sm font-medium text-foreground">{investorType === 'institutional' ? '72 hours' : '48 hours'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Status</span>
              <StatusBadge variant="pending" label="Under Review" dot />
            </div>
          </div>
          <div className="rounded-lg bg-info/5 border border-info/20 p-4 flex gap-3 text-left">
            <Info size={15} className="text-info flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              You will receive email updates as your application progresses through compliance review. Once approved, portfolio access will be automatically granted. Save your reference ID for tracking purposes.
            </p>
          </div>
          <button
            onClick={() => { setSubmitted(false); setCurrentStep('type'); setInvestorType(null); setUploadedFiles([]); setLivenessStatus('idle'); }}
            className="btn-secondary text-sm"
          >
            Start New Application
          </button>
        </div>
      </div>
    );
  }

  // ─── Step: Investor Type ──────────────────────────────────────────────────────
  const renderTypeStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Select Investor Type</h3>
        <p className="text-sm text-muted-foreground">Choose the type of investor account you are registering. This determines the compliance documents and verification steps required.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Individual */}
        <button
          onClick={() => { setInvestorType('individual'); setCurrentStep('profile'); }}
          className={`group text-left p-6 rounded-xl border-2 transition-all duration-200 hover:border-primary hover:bg-primary/3 ${
            investorType === 'individual' ? 'border-primary bg-primary/5' : 'border-border bg-surface-2'
          }`}
        >
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
            <User size={22} className="text-primary" />
          </div>
          <h4 className="font-semibold text-foreground mb-1">Individual Investor</h4>
          <p className="text-xs text-muted-foreground mb-4">Natural persons investing in their own name. Includes retail and accredited individual investors.</p>
          <div className="space-y-1.5">
            {['Government-issued photo ID', 'Proof of address', 'Liveness / biometric check', 'Suitability questionnaire'].map(item => (
              <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check size={12} className="text-positive flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-primary">
            <span>Start KYC</span>
            <ArrowRight size={13} />
          </div>
        </button>

        {/* Institutional */}
        <button
          onClick={() => { setInvestorType('institutional'); setCurrentStep('profile'); }}
          className={`group text-left p-6 rounded-xl border-2 transition-all duration-200 hover:border-primary hover:bg-primary/3 ${
            investorType === 'institutional' ? 'border-primary bg-primary/5' : 'border-border bg-surface-2'
          }`}
        >
          <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center mb-4 group-hover:bg-warning/15 transition-colors">
            <Building2 size={22} className="text-warning" />
          </div>
          <h4 className="font-semibold text-foreground mb-1">Institutional Investor</h4>
          <p className="text-xs text-muted-foreground mb-4">Corporate entities, funds, trusts, and other legal persons. Requires full KYB verification.</p>
          <div className="space-y-1.5">
            {['Certificate of incorporation', 'Memorandum & articles', 'UBO declarations (≥25%)', 'FATCA / CRS classification'].map(item => (
              <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check size={12} className="text-positive flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-warning">
            <span>Start KYB</span>
            <ArrowRight size={13} />
          </div>
        </button>
      </div>

      {/* Info notice */}
      <div className="rounded-lg bg-surface-2 border border-border p-4 flex gap-3">
        <Lock size={15} className="text-muted-foreground flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-foreground mb-0.5">Secure & Encrypted Submission</p>
          <p className="text-xs text-muted-foreground">All submitted documents and personal data are encrypted at rest and in transit. Your information is processed solely for regulatory compliance purposes in accordance with applicable AML/CFT regulations.</p>
        </div>
      </div>
    </div>
  );

  // ─── Step: Individual Profile ─────────────────────────────────────────────────
  const renderIndividualProfile = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Personal Information</h3>
        <p className="text-sm text-muted-foreground">Provide your personal details exactly as they appear on your government-issued identification document.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { label: 'First Name', key: 'firstName', placeholder: 'As on ID', required: true },
          { label: 'Last Name', key: 'lastName', placeholder: 'As on ID', required: true },
          { label: 'Email Address', key: 'email', type: 'email', placeholder: 'your@email.com', required: true },
          { label: 'Phone Number', key: 'phone', placeholder: '+1 (555) 000-0000', required: true },
          { label: 'Date of Birth', key: 'dob', type: 'date', required: true },
          { label: 'Nationality', key: 'nationality', placeholder: 'e.g. United States', required: true },
          { label: 'Country of Residence', key: 'country', placeholder: 'e.g. United Kingdom', required: true },
          { label: 'Tax Identification Number', key: 'taxId', placeholder: 'SSN / TIN / NIN', required: true },
        ].map(field => (
          <div key={field.key}>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {field.label} {field.required && <span className="text-negative">*</span>}
            </label>
            <input
              type={field.type || 'text'}
              placeholder={field.placeholder}
              value={(indProfile as Record<string, string>)[field.key] || ''}
              onChange={e => setIndProfile(prev => ({ ...prev, [field.key]: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Residential Address <span className="text-negative">*</span></label>
          <input
            type="text"
            placeholder="Street address, apartment, suite"
            value={indProfile.address}
            onChange={e => setIndProfile(prev => ({ ...prev, address: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">City</label>
          <input
            type="text"
            placeholder="City"
            value={indProfile.city}
            onChange={e => setIndProfile(prev => ({ ...prev, city: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Postal Code</label>
          <input
            type="text"
            placeholder="Postal / ZIP code"
            value={indProfile.postalCode}
            onChange={e => setIndProfile(prev => ({ ...prev, postalCode: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
      </div>

      {/* PEP Declaration */}
      <div className="rounded-xl bg-surface-2 border border-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Flag size={15} className="text-warning" />
          <p className="text-sm font-semibold text-foreground">Politically Exposed Person (PEP) Declaration</p>
        </div>
        <p className="text-xs text-muted-foreground">Are you, or have you been within the last 12 months, a politically exposed person (PEP) or a close associate of a PEP?</p>
        <div className="flex gap-3">
          {['yes', 'no', 'close-associate'].map(opt => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="pepStatus"
                value={opt}
                checked={indProfile.pepStatus === opt}
                onChange={e => setIndProfile(prev => ({ ...prev, pepStatus: e.target.value }))}
                className="accent-primary"
              />
              <span className="text-sm text-foreground capitalize">{opt === 'close-associate' ? 'Close Associate' : opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
            </label>
          ))}
        </div>
        {indProfile.pepStatus !== 'no' && (
          <div className="rounded-lg bg-warning/5 border border-warning/20 p-3 flex gap-2">
            <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">PEP declarations trigger Enhanced Due Diligence (EDD). Additional documentation and a longer review period (5 business days) will apply.</p>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Step: Institutional Profile ─────────────────────────────────────────────
  const renderInstitutionalProfile = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Entity Information</h3>
        <p className="text-sm text-muted-foreground">Provide details about your legal entity. All information must match your official incorporation documents.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { label: 'Legal Entity Name', key: 'entityName', placeholder: 'Full registered name', required: true },
          { label: 'Entity Type', key: 'entityType', placeholder: 'e.g. Limited Company, Fund, Trust', required: true },
          { label: 'Jurisdiction of Incorporation', key: 'jurisdiction', placeholder: 'e.g. Cayman Islands', required: true },
          { label: 'Incorporation / Registration No.', key: 'incorporationNo', placeholder: 'Official registration number', required: true },
          { label: 'Primary Contact Name', key: 'primaryContact', placeholder: 'Authorised representative', required: true },
          { label: 'Contact Email', key: 'email', type: 'email', placeholder: 'compliance@entity.com', required: true },
          { label: 'Contact Phone', key: 'phone', placeholder: '+1 (555) 000-0000', required: true },
          { label: 'Website', key: 'website', placeholder: 'https://www.entity.com' },
          { label: 'Tax Identification Number (TIN)', key: 'taxId', placeholder: 'EIN / UTR / TIN', required: true },
        ].map(field => (
          <div key={field.key} className={field.key === 'entityName' ? 'md:col-span-2' : ''}>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {field.label} {field.required && <span className="text-negative">*</span>}
            </label>
            <input
              type={field.type || 'text'}
              placeholder={field.placeholder}
              value={(instProfile as Record<string, string>)[field.key] || ''}
              onChange={e => setInstProfile(prev => ({ ...prev, [field.key]: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Registered Address <span className="text-negative">*</span></label>
          <input
            type="text"
            placeholder="Full registered office address"
            value={instProfile.registeredAddress}
            onChange={e => setInstProfile(prev => ({ ...prev, registeredAddress: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">FATCA / CRS Classification <span className="text-negative">*</span></label>
          <select
            value={instProfile.fatcaStatus}
            onChange={e => setInstProfile(prev => ({ ...prev, fatcaStatus: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          >
            <option value="">Select FATCA/CRS classification…</option>
            <option>US Person / US Entity</option>
            <option>Financial Institution (FI)</option>
            <option>Active Non-Financial Foreign Entity (Active NFFE)</option>
            <option>Passive Non-Financial Foreign Entity (Passive NFFE)</option>
            <option>Exempt Beneficial Owner</option>
            <option>Non-Participating FFI</option>
          </select>
        </div>
      </div>
    </div>
  );

  // ─── Step: Documents ──────────────────────────────────────────────────────────
  const renderDocuments = () => {
    const requiredDocs = investorType === 'institutional'
      ? [
          { label: 'Certificate of Incorporation', hint: 'PDF, JPG, PNG — max 10 MB', required: true },
          { label: 'Memorandum & Articles of Association', hint: 'PDF — max 20 MB', required: true },
          { label: 'Register of Directors', hint: 'PDF — max 10 MB', required: true },
          { label: 'Proof of Registered Address', hint: 'Utility bill or bank statement (< 3 months)', required: true },
          { label: 'Source of Funds Declaration', hint: 'Signed letter on company letterhead', required: true },
          { label: 'Shareholder Register', hint: 'PDF — max 10 MB', required: false },
        ]
      : [
          { label: 'Primary Photo ID (Passport / National ID)', hint: 'PDF, JPG, PNG — max 10 MB', required: true },
          { label: 'Secondary ID (Driver\'s Licence)', hint: 'PDF, JPG, PNG — max 10 MB', required: false },
          { label: 'Proof of Address', hint: 'Utility bill or bank statement (< 3 months)', required: true },
          { label: 'Source of Funds / Wealth Declaration', hint: 'Bank statement, payslip, or signed letter', required: true },
        ];

    const cleanCount = uploadedFiles.filter(f => f.status === 'verified').length;
    const scanningCount = uploadedFiles.filter(f => f.status === 'scanning').length;

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            {investorType === 'institutional' ? 'Corporate Documents' : 'Identity Documents'}
          </h3>
          <p className="text-sm text-muted-foreground">Upload clear, legible copies of all required documents. Each file is automatically virus-scanned before being added to the secure document vault.</p>
        </div>

        {/* Scan status summary */}
        {uploadedFiles.length > 0 && (
          <div className="rounded-xl border border-border bg-surface-2 p-3.5 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <ScanLine size={14} className="text-info" />
              <span className="text-xs font-semibold text-foreground">Virus Scan Status</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-positive"><ShieldCheck size={12} /> {cleanCount} clean</span>
              {scanningCount > 0 && <span className="flex items-center gap-1 text-warning animate-pulse"><ScanLine size={12} /> {scanningCount} scanning</span>}
              <span className="text-muted-foreground">Engine: ClamAV</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {requiredDocs.map(doc => (
            <div key={doc.label} className="rounded-xl bg-surface-2 border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={15} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">{doc.label}</span>
                </div>
                {doc.required
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-negative/10 text-negative font-medium">Required</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-muted-foreground font-medium">Optional</span>
                }
              </div>
              <UploadZone
                label="Click or drag to upload"
                hint={doc.hint}
                onUpload={() => handleAddFile(doc.label)}
              />
            </div>
          ))}
        </div>

        {uploadedFiles.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Uploaded Files ({uploadedFiles.length})</p>
            {uploadedFiles.map(file => (
              <FileRow key={file.id} file={file} onRemove={handleRemoveFile} />
            ))}
          </div>
        )}

        <div className="rounded-lg bg-info/5 border border-info/20 p-4 flex gap-3">
          <Info size={15} className="text-info flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-xs font-semibold text-foreground">Secure Document Vault</p>
            <p className="text-xs text-muted-foreground">All uploaded documents are virus-scanned (ClamAV), encrypted at rest, and stored in the compliance document vault with a 7-year retention policy. Every upload is logged in the immutable audit trail.</p>
          </div>
        </div>
      </div>
    );
  };

  // ─── Step: Liveness Check ─────────────────────────────────────────────────────
  const renderLiveness = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Liveness Verification</h3>
        <p className="text-sm text-muted-foreground">Complete a real-time biometric liveness check to confirm your identity. This step requires camera access and takes approximately 2 minutes.</p>
      </div>

      <div className="glass-card rounded-xl p-6 flex flex-col items-center gap-5 text-center">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center border-2 transition-all ${
          livenessStatus === 'passed' ? 'border-positive bg-positive/10' :
          livenessStatus === 'pending'? 'border-warning bg-warning/10 animate-pulse' : 'border-border bg-surface-3'
        }`}>
          {livenessStatus === 'passed'
            ? <CheckCircle size={36} className="text-positive" />
            : <Camera size={36} className={livenessStatus === 'pending' ? 'text-warning' : 'text-muted-foreground'} />
          }
        </div>

        <div>
          <p className="font-semibold text-foreground text-base">
            {livenessStatus === 'idle' && 'Ready to Verify'}
            {livenessStatus === 'pending' && 'Verification In Progress…'}
            {livenessStatus === 'passed' && 'Liveness Check Passed ✓'}
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-sm mx-auto">
            {livenessStatus === 'idle' && 'Click the button below to initiate your biometric liveness check. You will be guided through a selfie and challenge sequence.'}
            {livenessStatus === 'pending' && 'Please follow the on-screen instructions from the verification provider. Keep your face centred and well-lit.'}
            {livenessStatus === 'passed' && 'Biometric match confirmed. Confidence score: 98.4%. Your selfie has been matched to your submitted ID document.'}
          </p>
        </div>

        {livenessStatus === 'idle' && (
          <button
            onClick={() => { setLivenessStatus('pending'); setTimeout(() => setLivenessStatus('passed'), 2500); }}
            className="btn-primary gap-2"
          >
            <Camera size={15} />
            Initiate Liveness Check
          </button>
        )}
        {livenessStatus === 'pending' && (
          <div className="flex items-center gap-2 text-warning text-sm">
            <RefreshCw size={14} className="animate-spin" />
            <span>Awaiting biometric challenge completion…</span>
          </div>
        )}
        {livenessStatus === 'passed' && (
          <div className="flex items-center gap-2 text-positive text-sm font-medium">
            <CheckCircle size={14} />
            <span>Verified — {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { icon: Camera, label: 'Selfie Capture', desc: 'Real-time face scan with anti-spoof detection' },
          { icon: Shield, label: 'Document Match', desc: 'Biometric match against submitted photo ID' },
          { icon: Lock, label: 'Encrypted Session', desc: 'End-to-end encrypted eKYC provider session' },
        ].map(item => (
          <div key={item.label} className="rounded-xl bg-surface-2 border border-border p-4 flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <item.icon size={15} className="text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground">{item.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-surface-2 border border-border p-4 flex gap-3">
        <Info size={15} className="text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          Liveness verification is powered by an integrated eKYC provider (Onfido / Jumio / Veriff). Your biometric data is processed solely for identity verification and is not stored beyond the verification session.
        </p>
      </div>
    </div>
  );

  // ─── Step: UBO Declarations ───────────────────────────────────────────────────
  const renderUBO = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">UBO & Director Declarations</h3>
        <p className="text-sm text-muted-foreground">Declare all Ultimate Beneficial Owners (UBOs) holding 25% or more of the entity, and all directors/authorised signatories.</p>
      </div>

      <div className="rounded-lg bg-warning/5 border border-warning/20 p-4 flex gap-3">
        <AlertTriangle size={15} className="text-warning flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          Failure to disclose all UBOs holding ≥25% beneficial ownership is a criminal offence under applicable AML/CFT legislation. All declared UBOs will be subject to individual identity verification and sanctions screening.
        </p>
      </div>

      <div className="space-y-4">
        {ubos.map((ubo, idx) => (
          <div key={ubo.id} className="rounded-xl bg-surface-2 border border-border p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-primary">{idx + 1}</span>
                </div>
                <span className="text-sm font-semibold text-foreground">UBO / Director {idx + 1}</span>
              </div>
              {ubos.length > 1 && (
                <button
                  onClick={() => handleRemoveUBO(ubo.id)}
                  className="p-1.5 rounded hover:bg-negative/10 text-muted-foreground hover:text-negative transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { label: 'Full Legal Name', key: 'name', placeholder: 'As on passport' },
                { label: 'Nationality', key: 'nationality', placeholder: 'Country of citizenship' },
                { label: 'Date of Birth', key: 'dob', type: 'date' },
                { label: 'Ownership Percentage (%)', key: 'ownership', placeholder: 'e.g. 35' },
              ].map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">{field.label}</label>
                  <input
                    type={field.type || 'text'}
                    placeholder={field.placeholder}
                    value={(ubo as Record<string, string>)[field.key] || ''}
                    onChange={e => setUbos(prev => prev.map(u => u.id === ubo.id ? { ...u, [field.key]: e.target.value } : u))}
                    className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ubo.isPEP}
                  onChange={e => setUbos(prev => prev.map(u => u.id === ubo.id ? { ...u, isPEP: e.target.checked } : u))}
                  className="accent-primary w-4 h-4"
                />
                <span className="text-sm text-foreground">This person is a Politically Exposed Person (PEP)</span>
              </label>
            </div>
            {ubo.isPEP && (
              <div className="rounded-lg bg-warning/5 border border-warning/20 p-3 flex gap-2">
                <Flag size={13} className="text-warning flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">PEP status will trigger Enhanced Due Diligence for this UBO. Additional documentation may be requested.</p>
              </div>
            )}
            <div className="pt-1">
              <p className="text-xs font-medium text-muted-foreground mb-2">Upload UBO Identity Document</p>
              <UploadZone
                label="Passport or National ID"
                hint="PDF, JPG, PNG — max 10 MB"
                onUpload={() => handleAddFile(`UBO_${idx + 1}_ID`)}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleAddUBO}
        className="btn-secondary gap-2 text-sm w-full"
      >
        <Plus size={14} />
        Add Another UBO / Director
      </button>
    </div>
  );

  // ─── Step: Suitability ────────────────────────────────────────────────────────
  const renderSuitability = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Investor Suitability Assessment</h3>
        <p className="text-sm text-muted-foreground">Complete this questionnaire to determine your investor classification and suitability for the investment products available on this platform.</p>
      </div>

      <div className="space-y-5">
        {[
          {
            label: 'Investment Experience',
            key: 'investmentExperience',
            options: ['No prior experience', 'Less than 2 years', '2–5 years', '5–10 years', 'More than 10 years'],
          },
          {
            label: 'Risk Tolerance',
            key: 'riskTolerance',
            options: ['Conservative (capital preservation)', 'Moderate (balanced growth)', 'Aggressive (high growth)', 'Speculative (maximum growth)'],
          },
          {
            label: 'Investment Horizon',
            key: 'investmentHorizon',
            options: ['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', 'More than 10 years'],
          },
          {
            label: 'Annual Income (USD)',
            key: 'annualIncome',
            options: ['Under $50,000', '$50,000 – $100,000', '$100,000 – $250,000', '$250,000 – $500,000', 'Over $500,000'],
          },
          {
            label: 'Net Worth (USD, excluding primary residence)',
            key: 'netWorth',
            options: ['Under $100,000', '$100,000 – $500,000', '$500,000 – $1,000,000', '$1,000,000 – $5,000,000', 'Over $5,000,000'],
          },
          {
            label: 'Primary Investment Objective',
            key: 'investmentObjective',
            options: ['Capital preservation', 'Income generation', 'Capital growth', 'Speculation / trading', 'Diversification'],
          },
          {
            label: 'Source of Investment Funds',
            key: 'sourceOfFunds',
            options: ['Employment income', 'Business income', 'Investment returns', 'Inheritance / gift', 'Asset sale proceeds', 'Other'],
          },
        ].map(q => (
          <div key={q.key} className="rounded-xl bg-surface-2 border border-border p-4 space-y-3">
            <p className="text-sm font-medium text-foreground">{q.label}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {q.options.map(opt => (
                <label key={opt} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                  (suitability as Record<string, string>)[q.key] === opt
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border bg-surface-3 text-muted-foreground hover:border-primary/40'
                }`}>
                  <input
                    type="radio"
                    name={q.key}
                    value={opt}
                    checked={(suitability as Record<string, string>)[q.key] === opt}
                    onChange={e => setSuitability(prev => ({ ...prev, [q.key]: e.target.value }))}
                    className="accent-primary"
                  />
                  <span className="text-xs">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {/* Accredited Investor Status */}
        <div className="rounded-xl bg-surface-2 border border-border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Landmark size={15} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Accredited / Sophisticated Investor Status</p>
          </div>
          <p className="text-xs text-muted-foreground">Do you qualify as an accredited or sophisticated investor under applicable securities regulations?</p>
          <div className="flex flex-wrap gap-3">
            {['Yes — Accredited Investor', 'Yes — Sophisticated Investor', 'No — Retail Investor'].map(opt => (
              <label key={opt} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs ${
                suitability.accreditedStatus === opt
                  ? 'border-primary bg-primary/5 text-foreground font-medium'
                  : 'border-border bg-surface-3 text-muted-foreground hover:border-primary/40'
              }`}>
                <input
                  type="radio"
                  name="accreditedStatus"
                  value={opt}
                  checked={suitability.accreditedStatus === opt}
                  onChange={e => setSuitability(prev => ({ ...prev, accreditedStatus: e.target.value }))}
                  className="accent-primary"
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Step: Review & Submit ────────────────────────────────────────────────────
  const renderReview = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Review & Submit Application</h3>
        <p className="text-sm text-muted-foreground">Review your application details before final submission. Once submitted, your application will enter the compliance review queue.</p>
      </div>

      {/* Summary cards */}
      <div className="space-y-3">
        <div className="rounded-xl bg-surface-2 border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-surface-3 flex items-center gap-2">
            <User size={14} className="text-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Application Summary</span>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">
            {[
              { label: 'Application Type', value: investorType === 'institutional' ? 'KYB — Corporate Entity' : 'KYC — Individual Investor' },
              { label: 'Review Queue', value: investorType === 'institutional' ? 'Corporate KYB Queue' : 'Standard KYC Queue' },
              { label: 'Expected SLA', value: investorType === 'institutional' ? '72 hours' : '48 hours' },
              { label: 'Documents Uploaded', value: `${uploadedFiles.length} file${uploadedFiles.length !== 1 ? 's' : ''}` },
              ...(investorType === 'institutional' ? [{ label: 'UBOs Declared', value: `${ubos.length} person${ubos.length !== 1 ? 's' : ''}` }] : []),
              ...(investorType === 'individual' ? [{ label: 'Liveness Check', value: livenessStatus === 'passed' ? 'Completed ✓' : 'Pending' }] : []),
            ].map(item => (
              <div key={item.label}>
                <p className="text-xs text-muted-foreground">{item.label}</p>
                <p className="text-sm font-medium text-foreground mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Checklist */}
        <div className="rounded-xl bg-surface-2 border border-border p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Pre-Submission Checklist</p>
          <div className="space-y-2">
            {[
              { label: 'All required fields completed', done: true },
              { label: 'Identity documents uploaded', done: uploadedFiles.length > 0 },
              { label: investorType === 'individual' ? 'Liveness check completed' : 'UBO declarations submitted', done: investorType === 'individual' ? livenessStatus === 'passed' : ubos.some(u => u.name) },
              { label: 'Suitability questionnaire answered', done: !!suitability.riskTolerance },
              { label: 'PEP declaration confirmed', done: true },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2.5">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-positive/10' : 'bg-surface-3'}`}>
                  {item.done
                    ? <Check size={11} className="text-positive" />
                    : <Clock size={11} className="text-muted-foreground" />
                  }
                </div>
                <span className={`text-xs ${item.done ? 'text-foreground' : 'text-muted-foreground'}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Consent */}
        <div className="rounded-xl bg-surface-2 border border-border p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Declarations & Consent</p>
          <div className="space-y-2.5">
            {[
              'I confirm that all information provided is accurate, complete, and not misleading.',
              'I consent to the processing of my personal data for AML/CFT compliance purposes.',
              'I understand that providing false information may result in application rejection and potential legal consequences.',
              'I agree to notify the platform of any material changes to the information provided.',
            ].map((text, i) => (
              <label key={i} className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" defaultChecked className="accent-primary mt-0.5 w-4 h-4 flex-shrink-0" />
                <span className="text-xs text-muted-foreground">{text}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        className="btn-primary w-full gap-2 py-3 text-sm font-semibold"
      >
        <Shield size={16} />
        Submit {investorType === 'institutional' ? 'KYB' : 'KYC'} Application
      </button>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'type': return renderTypeStep();
      case 'profile': return investorType === 'institutional' ? renderInstitutionalProfile() : renderIndividualProfile();
      case 'documents': return renderDocuments();
      case 'liveness': return renderLiveness();
      case 'ubo': return renderUBO();
      case 'suitability': return renderSuitability();
      case 'review': return renderReview();
      default: return null;
    }
  };

  const isFirstStep = currentStep === 'type';
  const isLastStep = currentStep === 'review';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield size={20} className="text-primary" />
            <h1 className="text-xl font-bold text-foreground">
              {investorType === 'institutional' ? 'KYB Submission Portal' : 'KYC Submission Portal'}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {investorType === 'institutional' ?'Complete your Know Your Business (KYB) verification to unlock portfolio access for your entity.' :'Complete your Know Your Customer (KYC) verification to unlock portfolio access.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Lock size={13} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">256-bit encrypted</span>
        </div>
      </div>

      {/* Stepper — only show after type selection */}
      {investorType && currentStep !== 'type' && (
        <div className="glass-card rounded-xl p-4">
          <Stepper steps={steps} currentStep={currentStep} />
        </div>
      )}

      {/* Step content */}
      <div className="glass-card rounded-xl p-6">
        {renderStep()}
      </div>

      {/* Navigation */}
      {currentStep !== 'type' && currentStep !== 'review' && (
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="btn-secondary gap-2 text-sm"
          >
            <ChevronLeft size={15} />
            Back
          </button>
          <button
            onClick={handleNext}
            className="btn-primary gap-2 text-sm"
          >
            Continue
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
